import { Analyst, AnalystRole } from '../interfaces/Analyst';
import { Filters, Sorter } from '../interfaces/Api';
import axios from 'axios';
import BaseService from './BaseService';
import Configuration from './ServiceConfig';

interface Response {
	data: Analyst[];
	totalCount: number;
}

interface AnalystProfile {
	firstName: string;
	lastName: string;
	email: string;
	globalPartner: number;
	clients: number[];
}

class AnalystsService extends BaseService {
	config: Configuration;
	path: string;
	token: string | null;

	statusOption = [
		{ value: '0', label: 'Suspended' },
		{ value: '1', label: 'Active' },
		{ value: '2', label: 'Invited' },
	];

	analystProfiles = [
		// { value: '0', label: 'Admin' },
		{ value: '1', label: 'Global Partner Admin' },
		{ value: '2', label: 'Analyst' },
	];

	constructor() {
		super();
		this.config = new Configuration();
		this.path = `${this.config.baseurl}${'analysts/'}`;
		this.token = localStorage.getItem('token');
	}

	async createNewAnalyst(analyst: AnalystProfile) {
		return axios
			.post(`${this.path}`, analyst, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then((response) => {
				return response.data;
			})
			.catch((err) => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async resendInvitation(id: number) {
		return axios
			.post(`${this.path}${id}/reinvite`, undefined, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then((response) => {
				return response.data;
			})
			.catch((err) => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async get(id: number) {
		return axios
			.get(`${this.path}${id}`, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then((response) => response.data)
			.catch((err) => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	private parseDataTable(results: any[]): Analyst[] {
		return results.map(object => {
			return {
				key: object.id,
				analyst: {
					name: object.firstName,
					surname: object.lastName,
					email: object.email,
				},
				clients: object.role !== AnalystRole.GLOBAL_PARTNER_ADMIN ? object.clients : object.globalPartner?.clients,
				globalPartner: object.globalPartner?.name,
				profile: object.role,
				status: object.status,
			};
		});
	}

	async getAll(filters: Filters, sorter: Sorter, limit: number = 10, offset: number = 0): Promise<Response | void> {
		let params = {};

		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value) {
					params = { ...params, [key]: value };
				}
			});
		}

		if (sorter && sorter.sort) {
			params = { ...params, ...sorter };
		}

		params = { ...params, limit, offset };

		return axios
			.get(`${this.path}`, {
				params,
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then((response) => {
				return {
					data: this.parseDataTable(response.data.rows),
					totalCount: response.data.count,
				};
			})
			.catch((err) => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async update(id: number, analyst: AnalystProfile) {
		return axios
			.patch(`${this.path}${id}`, analyst, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then((response) => {
				return response.data;
			})
			.catch((err) => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	async delete(id: number) {
		return axios
			.delete(`${this.path}${id}`, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			})
			.then(response => {
				return response.data;
			})
			.catch(err => {
				if (err.response) {
					this.handleResponseError(err.response);
				} else {
					this.handleError(err);
				}
			});
	}

	getAllProfileOptions() {
		return this.analystProfiles;
	}

	getAllStatusOptions() {
		return this.statusOption;
	}
}

export default AnalystsService;
